/**
 * Cart Performance Optimizations
 * Optimized cart operations for better performance and user experience
 */

// Cache for frequently accessed cart data
window.cartCache = {
  data: null,
  timestamp: 0,
  ttl: 5000, // 5 seconds cache
  
  get() {
    const now = Date.now();
    if (this.data && (now - this.timestamp) < this.ttl) {
      return this.data;
    }
    return null;
  },
  
  set(data) {
    this.data = data;
    this.timestamp = Date.now();
  },
  
  clear() {
    this.data = null;
    this.timestamp = 0;
  }
};

// Debounced cart operations to prevent excessive API calls
const debouncedOperations = new Map();

function debounce(fn, delay, key) {
  if (debouncedOperations.has(key)) {
    clearTimeout(debouncedOperations.get(key));
  }
  
  const timeoutId = setTimeout(() => {
    fn();
    debouncedOperations.delete(key);
  }, delay);
  
  debouncedOperations.set(key, timeoutId);
}

// Optimized cart refresh with caching (webhook-safe)
window.refreshCartOptimized = async function() {
  // Check cache first
  const cachedCart = window.cartCache.get();
  if (cachedCart) {
    return cachedCart;
  }
  
  try {
    const safeHeaders = window.createWebhookSafeHeaders ? 
      window.createWebhookSafeHeaders() : 
      window.getCartHeaders();
      
    const [cartPricesResponse, cartDataResponse] = await Promise.all([
      fetch('/cart?view=rebuild', {
        headers: safeHeaders
      }),
      fetch('/cart.json', {
        headers: safeHeaders
      })
    ]);
    
    const [cartPrices, cartData] = await Promise.all([
      cartPricesResponse.json(),
      cartDataResponse.json()
    ]);
    
    if (cartData.items.length === 0) {
      window.cartCache.clear();
      return { items: [] };
    }
    
    // Process cart items with updated pricing
    const optimizedItems = cartData.items.map(item => {
      const updatedItem = cartPrices.items.find(priceItem => 
        priceItem.variant_id === item.variant_id
      );
      
      return updatedItem ? {
        ...item,
        properties: {
          ...item.properties,
          '_price': `${updatedItem.price}`
        }
      } : item;
    }).filter(Boolean);
    
    const result = { ...cartData, items: optimizedItems };
    window.cartCache.set(result);
    
    return result;
  } catch (error) {
    console.error('Cart refresh failed:', error);
    throw error;
  }
};

// Batch cart operations for better performance
window.batchCartOperations = async function(operations) {
  const batches = [];
  const batchSize = 50; // Reduced from 100 for better performance
  
  for (let i = 0; i < operations.length; i += batchSize) {
    batches.push(operations.slice(i, i + batchSize));
  }
  
  const results = [];
  
  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    
    try {
      const safeHeaders = window.createWebhookSafeHeaders ? 
        window.createWebhookSafeHeaders() : 
        window.getCartHeaders();
        
      const batchResult = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: safeHeaders,
        body: JSON.stringify({ items: batch })
      });
      
      const result = await batchResult.json();
      results.push(result);
      
      // Progress notification
      document.dispatchEvent(new CustomEvent('queue-job:step', { 
        bubbles: true,
        detail: { completed: i + 1, total: batches.length }
      }));
      
      // Small delay between batches to prevent overwhelming the server
      if (i < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } catch (error) {
      console.error(`Batch ${i + 1} failed:`, error);
      throw error;
    }
  }
  
  return results;
};

// Smart cart rebuild with intelligent item merging and updates
window.rebuildCartOptimized = async function() {
  return new Promise(async (resolve, reject) => {
    try {
      console.log('🔄 Starting smart cart rebuild...');
      
      // Disable UI elements at start
      const qtyInputs = document.querySelectorAll('quantity-input');
      qtyInputs.forEach(el => {
        const input = el.querySelector('.qty-input__input');
        const minusBtn = el.querySelector('.btn--minus');
        const plusBtn = el.querySelector('.btn--plus');
        
        if (input) input.disabled = true;
        if (minusBtn) minusBtn.disabled = true;
        if (plusBtn) plusBtn.disabled = true;
      });
      
      // Get current cart state and target items using webhook-safe headers
      const cartHeaders = window.createWebhookSafeHeaders ? 
        window.createWebhookSafeHeaders() : 
        window.getCartHeaders();
      
      const [currentCartResponse, targetCartData] = await Promise.all([
        fetch('/cart.json', { headers: cartHeaders }),
        refreshCartOptimized()
      ]);
      
      const currentCart = await currentCartResponse.json();
      const targetItems = targetCartData.items.filter(item => 
        window.settings.dummy.product_id !== item.product_id
      );
      
      document.dispatchEvent(new CustomEvent('queue-job:step', { bubbles: true }));
      
      if (targetItems.length === 0) {
        // Clear cart completely if no target items (webhook-safe)
        const clearHeaders = window.createWebhookSafeHeaders ? 
          window.createWebhookSafeHeaders() : 
          window.getCartHeaders();
        
        await fetch('/cart/clear.js', { headers: clearHeaders });
        
        // Re-enable UI and resolve
        qtyInputs.forEach(el => {
          const input = el.querySelector('.qty-input__input');
          const minusBtn = el.querySelector('.btn--minus');
          const plusBtn = el.querySelector('.btn--plus');
          
          if (input) input.disabled = false;
          if (minusBtn) minusBtn.disabled = false;
          if (plusBtn) plusBtn.disabled = false;
        });
        return resolve();
      }
      
      // Create maps for efficient comparison
      const currentItemsMap = new Map();
      currentCart.items.forEach(item => {
        const key = `${item.variant_id}_${JSON.stringify(item.properties || {})}`;
        currentItemsMap.set(key, item);
      });
      
      const targetItemsMap = new Map();
      targetItems.forEach(item => {
        const key = `${item.variant_id}_${JSON.stringify(item.properties || {})}`;
        if (targetItemsMap.has(key)) {
          // Merge identical items (same variant + properties)
          const existing = targetItemsMap.get(key);
          existing.quantity += item.quantity;
        } else {
          targetItemsMap.set(key, {
            variant_id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
          });
        }
      });
      
      console.log(`📊 Cart comparison: ${currentItemsMap.size} current items, ${targetItemsMap.size} target items`);
      
      // Calculate changes needed
      const changes = {
        toAdd: [],
        toUpdate: [],
        toRemove: []
      };
      
      // Find items to add or update
      targetItemsMap.forEach((targetItem, key) => {
        const currentItem = currentItemsMap.get(key);
        if (!currentItem) {
          // New item - add it
          changes.toAdd.push({
            id: targetItem.variant_id,
            quantity: targetItem.quantity,
            properties: targetItem.properties
          });
        } else if (currentItem.quantity !== targetItem.quantity) {
          // Existing item with different quantity - update it
          changes.toUpdate.push({
            id: currentItem.id, // Use line item ID for updates
            quantity: targetItem.quantity
          });
        }
        // If quantities match, no change needed
      });
      
      // Find items to remove
      currentItemsMap.forEach((currentItem, key) => {
        if (!targetItemsMap.has(key)) {
          changes.toRemove.push({
            id: currentItem.id, // Use line item ID for removal
            quantity: 0
          });
        }
      });
      
      console.log(`🔄 Changes needed: ${changes.toAdd.length} add, ${changes.toUpdate.length} update, ${changes.toRemove.length} remove`);
      
      document.dispatchEvent(new CustomEvent('queue-job:step', { bubbles: true }));
      
      // Apply changes efficiently
      const operations = [];
      
      // Remove items first (set quantity to 0)
      if (changes.toRemove.length > 0) {
        const safeHeaders = window.createWebhookSafeHeaders ? 
          window.createWebhookSafeHeaders() : 
          window.getCartHeaders();
          
        operations.push(
          fetch(`${theme.routes.cartUpdate}.js`, {
            method: 'POST',
            headers: safeHeaders,
            body: JSON.stringify({
              updates: Object.fromEntries(
                changes.toRemove.map(item => [item.id, 0])
              )
            })
          })
        );
      }
      
      // Update existing items
      if (changes.toUpdate.length > 0) {
        const safeHeaders = window.createWebhookSafeHeaders ? 
          window.createWebhookSafeHeaders() : 
          window.getCartHeaders();
          
        operations.push(
          fetch(`${theme.routes.cartUpdate}.js`, {
            method: 'POST',
            headers: safeHeaders,
            body: JSON.stringify({
              updates: Object.fromEntries(
                changes.toUpdate.map(item => [item.id, item.quantity])
              )
            })
          })
        );
      }
      
      // Add new items
      if (changes.toAdd.length > 0) {
        if (changes.toAdd.length >= 50) {
          // Use batch operations for large additions
          document.dispatchEvent(new CustomEvent('queue-job:increase-max', { 
            bubbles: true, 
            detail: Math.ceil(changes.toAdd.length / 50) 
          }));
          operations.push(batchCartOperations(changes.toAdd));
        } else {
          const safeHeaders = window.createWebhookSafeHeaders ? 
            window.createWebhookSafeHeaders() : 
            window.getCartHeaders();
            
          operations.push(
            fetch(`${theme.routes.cartAdd}.js`, {
              method: 'POST',
              headers: safeHeaders,
              body: JSON.stringify({ items: changes.toAdd })
            })
          );
        }
      }
      
      // Execute all operations
      await Promise.all(operations);
      
      document.dispatchEvent(new CustomEvent('queue-job:step', { bubbles: true }));
      
      // Clear attributes only (not the entire cart contents)
      await Promise.all([
        window.clearDiscounts(),
        window.clearCartAtributes() // Now only clears attributes, not cart contents
      ]);
      
      // Refresh cart attributes
      await window.refreshCartAttributes();
      
      document.dispatchEvent(new CustomEvent('queue-job:step', { bubbles: true }));
      
      // Update UI with final cart state
      const safeHeaders = window.createWebhookSafeHeaders ? 
        window.createWebhookSafeHeaders() : 
        window.getCartHeaders();
        
      const finalCart = await fetch('/cart.json', {
        headers: safeHeaders
      });
      
      const finalCartData = await finalCart.json();
      
      // Update UI quantity inputs
      finalCartData.items.forEach(item => {
        const qtyInput = document.querySelector(`.qty-input__input[data-variant-id="${item.variant_id}"]`);
        if (qtyInput) {
          qtyInput.closest('quantity-input').classList.add('in-cart');
          qtyInput.value = item.quantity;
        }
      });
      
      const cartItemsElement = document.querySelector('[data-cart-items]');
      if (cartItemsElement) {
        cartItemsElement.innerText = JSON.stringify(finalCartData.items);
      }
      
      document.dispatchEvent(new CustomEvent('queue-job:step', { bubbles: true }));
      
      // Render cart updates
      await window.render();
      
      // Re-enable UI elements
      qtyInputs.forEach(el => {
        const input = el.querySelector('.qty-input__input');
        const minusBtn = el.querySelector('.btn--minus');
        const plusBtn = el.querySelector('.btn--plus');
        
        if (input) input.disabled = false;
        if (minusBtn) minusBtn.disabled = false;
        if (plusBtn) plusBtn.disabled = false;
      });
      
      document.dispatchEvent(new CustomEvent('queue-job:step', { bubbles: true }));
      
      console.log('✅ Smart cart rebuild completed');
      resolve();
      
    } catch (error) {
      console.error('❌ Cart rebuild failed:', error);
      
      // Re-enable UI on error
      const qtyInputs = document.querySelectorAll('quantity-input');
      qtyInputs.forEach(el => {
        const input = el.querySelector('.qty-input__input');
        const minusBtn = el.querySelector('.btn--minus');
        const plusBtn = el.querySelector('.btn--plus');
        
        if (input) input.disabled = false;
        if (minusBtn) minusBtn.disabled = false;
        if (plusBtn) plusBtn.disabled = false;
      });
      
      reject(error);
    }
  });
};

// Optimized cart attribute refresh with reduced API calls
window.refreshCartAttributesOptimized = async function() {
  return new Promise(async (resolve, reject) => {
    try {
      // Single API call to check user status (webhook-safe)
      const safeHeaders = window.createWebhookSafeHeaders ? 
        window.createWebhookSafeHeaders() : 
        window.getCartHeaders();
        
      const stroetmannCartResponse = await fetch('/cart?view=json', {
        headers: safeHeaders
      });
      
      const { current_user, current_date, current_user_alias } = await stroetmannCartResponse.json();
      
      if (!current_user) {
        // Handle logout scenario
        const formData = new FormData();
        formData.append('attributes[login_refresh]', 'false');
        
        const logoutHeaders = window.createWebhookSafeHeaders ? 
          window.createWebhookSafeHeaders() : 
          window.getCartHeaders();
        // Need to remove Content-Type for FormData
        delete logoutHeaders['Content-Type'];
          
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: logoutHeaders,
          body: formData,
        });
        
        window.location.href = '/account/logout';
        return;
      }
      
      // Batch update all attributes in single request
      const formData = new FormData();
      formData.append('attributes[current_date]', current_date);
      formData.append('attributes[current_user]', current_user);
      formData.append('attributes[login_refresh]', 'false');
      if(current_user_alias) formData.append('attributes[current_user_alias]', current_user_alias);
      
      const updateHeaders = window.createWebhookSafeHeaders ? 
        window.createWebhookSafeHeaders() : 
        window.getCartHeaders();
      // Need to remove Content-Type for FormData
      delete updateHeaders['Content-Type'];
        
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: updateHeaders,
        body: formData,
      });
      
      resolve();
    } catch (error) {
      console.error('Cart attributes refresh failed:', error);
      reject(error);
    }
  });
};

// Debounced cart operations
window.debouncedCartUpdate = function(updateFn, delay = 300) {
  debounce(updateFn, delay, 'cart-update');
};

// Performance monitoring
const performanceMonitor = {
  start(operation) {
    this[operation] = performance.now();
  },
  
  end(operation) {
    if (this[operation]) {
      const duration = performance.now() - this[operation];
      console.log(`${operation} took ${duration.toFixed(2)}ms`);
      delete this[operation];
      return duration;
    }
    return 0;
  }
};

// Override existing functions with optimized versions
if (typeof window.originalRefreshCart === 'undefined') {
  window.originalRefreshCart = window.refreshCart;
  window.originalRebuildCart = window.rebuildCart;
  window.originalRefreshCartAttributes = window.refreshCartAttributes;
}

// Enable optimized functions
window.enableCartOptimizations = function() {
  window.refreshCart = window.refreshCartOptimized;
  window.rebuildCart = window.rebuildCartOptimized;
  window.refreshCartAttributes = window.refreshCartAttributesOptimized;
  console.log('Cart optimizations enabled');
};

// Disable optimized functions (fallback)
window.disableCartOptimizations = function() {
  window.refreshCart = window.originalRefreshCart;
  window.rebuildCart = window.originalRebuildCart;
  window.refreshCartAttributes = window.originalRefreshCartAttributes;
  window.cartCache.clear();
  console.log('Cart optimizations disabled');
};

// Auto-enable optimizations
document.addEventListener('DOMContentLoaded', () => {
  if (window.settings && window.settings.enableCartOptimizations !== false) {
    window.enableCartOptimizations();
  }
});

console.log('Cart performance optimizations loaded');